import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, useTheme, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Chip, IconButton, Tooltip, alpha, Collapse
} from '@mui/material';
import CellTowerIcon from '@mui/icons-material/CellTower';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import BugReportIcon from '@mui/icons-material/BugReport';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MapIcon from '@mui/icons-material/Map';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { analyticsApi } from '../../services/api';

const CHART_COLORS = ['#7c4dff', '#b388ff', '#d1c4e9', '#ede7f6', '#9575cd', '#673ab7'];

const HighMastSummarySection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { getUserOrganization } = useAuth();
  const organizationId = getUserOrganization()?.id;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Expandable table state
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [tableData, setTableData] = useState({});
  const [tableLoading, setTableLoading] = useState({});
  const [tablePagination, setTablePagination] = useState({});

  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    if (!organizationId) return;
    try {
      setLoading(true);
      const response = await analyticsApi.getHighMastDashboardStats();
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching high mast stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Fetch detail table data
  const fetchTableData = useCallback(async (cardType, page = 0, rowsPerPage = 10) => {
    setTableLoading(prev => ({ ...prev, [cardType]: true }));
    try {
      const response = await analyticsApi.getHighMastDetails(cardType, {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      });
      setTableData(prev => ({ ...prev, [cardType]: response.data }));
    } catch (err) {
      console.error('Error fetching table data:', err);
    } finally {
      setTableLoading(prev => ({ ...prev, [cardType]: false }));
    }
  }, []);

  const handleExpandCard = (cardId, cardType) => {
    if (expandedCardId === cardId) {
      setExpandedCardId(null);
      return;
    }
    setExpandedCardId(cardId);
    if (!tablePagination[cardId]) {
      setTablePagination(prev => ({ ...prev, [cardId]: { page: 0, rowsPerPage: 10 } }));
    }
    if (!tableData[cardType]) {
      fetchTableData(cardType, 0, 10);
    }
  };

  const handlePageChange = (cardType, newPage) => {
    const rpp = tablePagination[expandedCardId]?.rowsPerPage || 10;
    setTablePagination(prev => ({ ...prev, [expandedCardId]: { page: newPage, rowsPerPage: rpp } }));
    fetchTableData(cardType, newPage, rpp);
  };

  const handleViewOnMap = (asset) => {
    if (asset.center_lat && asset.center_lng) {
      navigate('/map-view', {
        state: {
          selectedAsset: {
            id: asset.id,
            name: asset.name,
            code: asset.code,
            center_lat: asset.center_lat,
            center_lng: asset.center_lng,
          },
          flyTo: { lat: asset.center_lat, lng: asset.center_lng, zoom: 18 }
        }
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#7c4dff' }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (!stats) return null;

  // Prepare card data
  const inspected = stats.totalHighMasts - stats.highMastsWithoutInspection;
  const coveragePercent = stats.totalHighMasts > 0 ? Math.round((inspected / stats.totalHighMasts) * 100) : 0;

  const cards = [
    {
      id: 'total',
      cardType: 'total_high_masts',
      title: 'Total High Masts',
      count: stats.totalHighMasts,
      description: `${stats.subtypeBreakdown.map(s => `${s.count} ${s.subtype_name}`).join(', ') || 'No assets'}`,
      Icon: CellTowerIcon,
      color: '#7c4dff',
      chartData: stats.subtypeBreakdown.map((s, i) => ({ name: s.subtype_name, value: s.count, color: CHART_COLORS[i % CHART_COLORS.length] }))
    },
    {
      id: 'uninspected',
      cardType: 'uninspected',
      title: 'Uninspected',
      count: stats.highMastsWithoutInspection,
      description: `${coveragePercent}% coverage (${inspected}/${stats.totalHighMasts})`,
      Icon: AssignmentLateIcon,
      color: theme.palette.warning.main,
      chartData: [
        { name: 'Inspected', value: inspected, color: '#4caf50' },
        { name: 'Uninspected', value: stats.highMastsWithoutInspection, color: '#f44336' }
      ].filter(d => d.value > 0)
    },
    {
      id: 'defects',
      cardType: 'with_defects',
      title: 'Total Defects',
      count: stats.totalDefects,
      description: stats.defectsByType.length > 0 ? `${stats.defectsByType.length} defect types detected` : 'No defects recorded',
      Icon: BugReportIcon,
      color: theme.palette.error.main,
      chartData: stats.defectsByType.slice(0, 5).map((d, i) => ({ name: d.defect_type_name, value: d.count, color: CHART_COLORS[i % CHART_COLORS.length] }))
    },
    {
      id: 'top_asset',
      cardType: 'with_defects',
      title: 'Most Defective Asset',
      count: stats.topAssetsByDefects[0]?.defect_count || 0,
      description: stats.topAssetsByDefects[0]?.asset_code || 'No defects found',
      Icon: WarningIcon,
      color: theme.palette.secondary.main,
      chartData: null
    }
  ];

  return (
    <Box>
      {/* KPI Cards Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
        gap: 2.5,
        mb: 3
      }}>
        {cards.map((card) => (
          <Paper
            key={card.id}
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: `1px solid ${alpha(card.color, 0.15)}`,
              background: `linear-gradient(135deg, ${alpha(card.color, 0.04)}, ${alpha(card.color, 0.01)})`,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: `0 4px 20px ${alpha(card.color, 0.15)}`,
                borderColor: alpha(card.color, 0.3),
              },
              ...(expandedCardId === card.id && {
                borderColor: alpha(card.color, 0.5),
                boxShadow: `0 4px 20px ${alpha(card.color, 0.2)}`,
              })
            }}
            onClick={() => handleExpandCard(card.id, card.cardType)}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <card.Icon sx={{ color: card.color, fontSize: 22 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                    {card.title}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: card.color, mb: 0.5 }}>
                  {card.count}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                  {card.description}
                </Typography>
              </Box>
              {/* Mini donut chart */}
              {card.chartData && card.chartData.length > 0 && (
                <Box sx={{ width: 60, height: 60, flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={card.chartData} dataKey="value" cx="50%" cy="50%" innerRadius={16} outerRadius={28} paddingAngle={2} strokeWidth={0}>
                        {card.chartData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value, name) => [value, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              {expandedCardId === card.id ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Expandable Detail Table */}
      {expandedCardId && (
        <Collapse in={!!expandedCardId}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              {cards.find(c => c.id === expandedCardId)?.title} — Details
            </Typography>

            {tableLoading[cards.find(c => c.id === expandedCardId)?.cardType] ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha('#7c4dff', 0.04) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Asset Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Subtype</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Height</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Inspections</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Defects</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const cardType = cards.find(c => c.id === expandedCardId)?.cardType;
                        const data = tableData[cardType]?.data || [];
                        if (data.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={8} sx={{ py: 4, textAlign: 'center' }}>
                                <Typography color="text.secondary">No data available</Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }
                        return data.map((row) => (
                          <TableRow key={row.id} hover sx={{ '& .MuiTableCell-root': { py: 1.2 } }}>
                            <TableCell>
                              <Chip label={row.code} size="small" sx={{ fontSize: '0.75rem', fontWeight: 600, bgcolor: alpha('#7c4dff', 0.1), color: '#7c4dff' }} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.name}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.subtype}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.section || 'N/A'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{row.height ? `${row.height}m` : '—'}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={row.inspections?.count || 0} size="small"
                                sx={{ fontSize: '0.75rem', bgcolor: row.inspections?.count > 0 ? alpha('#4caf50', 0.1) : alpha('#f44336', 0.1), color: row.inspections?.count > 0 ? '#4caf50' : '#f44336' }} />
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={row.defectCount || 0} size="small"
                                sx={{ fontSize: '0.75rem', bgcolor: row.defectCount > 0 ? alpha('#f44336', 0.1) : alpha('#4caf50', 0.1), color: row.defectCount > 0 ? '#f44336' : '#4caf50' }} />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="View on Map">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleViewOnMap(row); }}
                                  sx={{ color: '#7c4dff', bgcolor: alpha('#7c4dff', 0.08), '&:hover': { bgcolor: alpha('#7c4dff', 0.15) } }}>
                                  <MapIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
                {(() => {
                  const cardType = cards.find(c => c.id === expandedCardId)?.cardType;
                  const pagination = tableData[cardType]?.pagination;
                  if (!pagination) return null;
                  return (
                    <TablePagination
                      component="div"
                      count={pagination.total}
                      page={tablePagination[expandedCardId]?.page || 0}
                      rowsPerPage={tablePagination[expandedCardId]?.rowsPerPage || 10}
                      onPageChange={(e, newPage) => handlePageChange(cardType, newPage)}
                      rowsPerPageOptions={[10]}
                    />
                  );
                })()}
              </>
            )}
          </Paper>
        </Collapse>
      )}
    </Box>
  );
};

export default HighMastSummarySection;
